import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  chooseOption,
  coveredIssues,
  defaultSelection,
  projectOperations,
  selectedOperations,
  type ApplyResponse,
  type FixSelection,
  type FixVerification,
  type RemediationEngine,
  type RemediationPlan,
  type RosterSite,
  type RosterSnapshot,
} from '../../../services/rosterCompliance';

export type FixFlowStep = 'loading' | 'choose' | 'review' | 'applying' | 'done' | 'error';

export interface ReviewCheck {
  status: 'idle' | 'checking' | 'ready' | 'error';
  verification: FixVerification | null;
  error: string;
}

const IDLE_CHECK: ReviewCheck = { status: 'idle', verification: null, error: '' };

/**
 * The fix flow as a small state machine: load suggestions → choose → review
 * (with a combined dry-run) → apply → done. Components render it; they hold no
 * flow state of their own.
 */
export function useComplianceFixes(engine: RemediationEngine, site: RosterSite, slotDate: string) {
  const [step, setStep] = useState<FixFlowStep>('loading');
  const [plan, setPlan] = useState<RemediationPlan | null>(null);
  const [selection, setSelection] = useState<FixSelection>({});
  const [clearedIssueKeys, setClearedIssueKeys] = useState<string[]>([]);
  const [review, setReview] = useState<ReviewCheck>(IDLE_CHECK);
  const [result, setResult] = useState<ApplyResponse | null>(null);
  const [error, setError] = useState('');

  // Each async step stamps a token; a reply for an older token is dropped, so a
  // slow request cannot overwrite a newer one or touch an unmounted dialog.
  const requestRef = useRef(0);
  useEffect(() => () => { requestRef.current += 1; }, []);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setStep('loading');
    setError('');
    setResult(null);
    setReview(IDLE_CHECK);
    setClearedIssueKeys([]);
    try {
      const nextPlan = await engine.suggest(site, slotDate);
      if (request !== requestRef.current) return;
      setPlan(nextPlan);
      setSelection(defaultSelection(nextPlan.issues));
      setStep('choose');
    } catch (err) {
      if (request !== requestRef.current) return;
      setError((err as Error).message);
      setStep('error');
    }
  }, [engine, site, slotDate]);

  useEffect(() => { void load(); }, [load]);

  const issues = useMemo(() => plan?.issues ?? [], [plan]);
  const operations = useMemo(() => selectedOperations(issues, selection), [issues, selection]);
  const covered = useMemo(() => coveredIssues(issues, selection), [issues, selection]);

  const guardName = useCallback((guardId: string) => plan?.guardNames[guardId] ?? '', [plan]);

  const projected = useMemo<RosterSnapshot | null>(
    () => (plan ? projectOperations(plan.snapshot, operations, guardName) : null),
    [guardName, operations, plan],
  );

  const choose = useCallback((issueKey: string, optionId: string | null) => {
    const change = chooseOption(issues, selection, issueKey, optionId);
    setSelection(change.selection);
    setClearedIssueKeys(change.cleared);
  }, [issues, selection]);

  const resetToRecommended = useCallback(() => {
    setSelection(defaultSelection(issues));
    setClearedIssueKeys([]);
  }, [issues]);

  const startReview = useCallback(async () => {
    if (!plan || operations.length === 0) return;
    const request = ++requestRef.current;
    setStep('review');
    setReview({ status: 'checking', verification: null, error: '' });
    try {
      const verification = await engine.previewChanges(plan, operations);
      if (request !== requestRef.current) return;
      setReview({ status: 'ready', verification, error: verification.error ?? '' });
    } catch (err) {
      if (request !== requestRef.current) return;
      setReview({ status: 'error', verification: null, error: (err as Error).message });
    }
  }, [engine, operations, plan]);

  const backToChoices = useCallback(() => {
    requestRef.current += 1;
    setReview(IDLE_CHECK);
    setStep('choose');
  }, []);

  const apply = useCallback(async () => {
    if (!plan || operations.length === 0) return null;
    const request = ++requestRef.current;
    setStep('applying');
    setError('');
    try {
      const response = await engine.applyChanges(plan, operations);
      if (request !== requestRef.current) return null;
      setResult(response);
      setStep('done');
      return response;
    } catch (err) {
      if (request !== requestRef.current) return null;
      // Nothing was written: the apply is one transaction. Stay on review so the
      // admin can go back, or start over against the roster as it now is.
      setError((err as Error).message);
      setStep('review');
      return null;
    }
  }, [engine, operations, plan]);

  return {
    step,
    plan,
    issues,
    selection,
    covered,
    clearedIssueKeys,
    operations,
    projected,
    review,
    result,
    error,
    guardName,
    reload: load,
    choose,
    resetToRecommended,
    startReview,
    backToChoices,
    apply,
  };
}

export type ComplianceFixFlow = ReturnType<typeof useComplianceFixes>;
